import { Box, chakra, Flex, Text } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import {
  LuCreditCard,
  LuChevronLeft,
  LuFileText,
  LuLifeBuoy,
  LuLogOut,
  LuMail,
  LuShield,
} from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { useBrand } from "brand/BrandConfig";
import { useAuthStore } from "stores/AuthStoreProvider";
import { CONTACT_URL } from "src/brand.config";
import { Disclaimer } from "src/layouts/AppLayout";
import { progressOf } from "src/models/progress";
import { Group, Row } from "src/components/SettingsList";
import { useChatStore } from "src/stores/chatStoreContext";
import { ACCENT_GRADIENT } from "src/theme";
import { membershipSummary } from "./membershipSummary";

export const SettingsPage = observer(() => {
  const brand = useBrand();
  const authStore = useAuthStore();
  const chatStore = useChatStore();
  const navigate = useNavigate();
  const user = authStore.user;

  if (!user) return null;
  const progress = progressOf({
    messagesSent: chatStore.messagesSent,
    companionsMet: chatStore.companionsMet,
    streakDays: chatStore.streakDays,
  });
  const email = user.data.email ?? "";

  return (
    <Box pb="calc(16px + env(safe-area-inset-bottom))">
      <Flex
        align="center"
        gap={1}
        px={2}
        pt="calc(8px + env(safe-area-inset-top))"
      >
        <chakra.button
          type="button"
          aria-label="Back"
          onClick={() => navigate("/")}
          p={2}
          borderRadius="full"
          _active={{ bg: "elevated" }}
        >
          <LuChevronLeft size={26} />
        </chakra.button>
        <Text fontWeight="semibold" fontSize="lg">
          Profile
        </Text>
      </Flex>

      <Flex
        direction="column"
        align="center"
        mt={6}
        gap={2}
        animation="tlRise 0.4s ease both"
      >
        <Flex
          boxSize="84px"
          borderRadius="full"
          background={ACCENT_GRADIENT}
          align="center"
          justify="center"
          fontSize="34px"
          fontWeight="bold"
          color="white"
          boxShadow="0 10px 30px rgba(255,98,87,0.3)"
        >
          {email.charAt(0).toUpperCase() || "?"}
        </Flex>
        <Text fontWeight="semibold">{email}</Text>
        <Text fontSize="sm" color="muted">
          Lv {progress.level} · {progress.title} · {progress.coins} coins
        </Text>
      </Flex>

      <Group title="Membership">
        <Row
          icon={<LuCreditCard size={16} />}
          label="Manage membership"
          detail={membershipSummary(user)}
          chevron
          onClick={() => navigate("/settings/membership")}
        />
      </Group>

      <Group title="Support & legal">
        <Row
          icon={<LuMail size={16} />}
          label="Email us"
          detail={brand.email}
          href={`mailto:${brand.email}`}
          chevron
        />
        <Row
          icon={<LuLifeBuoy size={16} />}
          label="Contact page"
          href={CONTACT_URL}
          chevron
        />
        <Row
          icon={<LuFileText size={16} />}
          label="Terms and conditions"
          href={brand.docLinks.termsUrl}
          chevron
        />
        <Row
          icon={<LuShield size={16} />}
          label="Privacy policy"
          href={brand.docLinks.privacyUrl}
          chevron
        />
      </Group>

      <Group>
        <Row
          icon={<LuLogOut size={16} />}
          label="Sign out"
          danger
          onClick={() => navigate("/logout")}
        />
      </Group>

      <Disclaimer />
    </Box>
  );
});
